import { CompanyEnrichPreviewPerson } from "@/pages/Private/pages/CompanyEnrich/schema/companyEnrich";
import {
	IPersonSummary,
	ListPersonGlobalSearchInfoDTO,
} from "@/pages/Private/redux/search/search.schema";
import { SENIORITY_OPTIONS } from "@/pages/Private/pages/CompanyEnrich/constants/seniority.constants";
import { ASSIGNED_TAB_STATUSES } from "@/pages/Private/pages/List/pages/Unassigned/assignedTabStatuses";

// A preview person is only shown in the results list when one of their matched
// or current experiences resolves to a company; the rest are dropped at render.
// Counts (badges, footer) must use this same predicate to stay in sync.
export const hasDisplayableCompany = (person: CompanyEnrichPreviewPerson): boolean =>
	person.experiences?.some((exp) => (exp.is_matched || exp.is_current) && exp.company?.id) ?? false;

// A contact counts as "in a campaign" only when its (primary) list membership has
// actually been assigned to one — i.e. its assignStatus is in the Assigned-tab set,
// the same source of truth the list-detail "Assigned" tab counts by. A bare
// campaignId / campaignConfig is NOT sufficient: a DRAFT membership can carry those
// without ever having been enrolled, which over-counts. The company-card badges
// share this predicate so they agree with the Assigned tab.
export const isContactInCampaign = (contact: ListPersonGlobalSearchInfoDTO): boolean =>
	contact.assignStatus ? ASSIGNED_TAB_STATUSES.includes(contact.assignStatus) : false;

// The minimal company shape the lead-style card renders. The backend-enriched
// `matchedCompany` carries all of these; the nested experience company carries
// everything except `description`.
export interface DisplayCompany {
	name?: string | null;
	domain?: string | null;
	logo_url?: string | null;
	industry?: string | null;
	description?: string | null;
}

// The experience a person is displayed under (its company and job title): a
// matched experience with a company id, else the current one. Mirrors the backend
// `selectPrimaryExperienceCompany` and the `hasDisplayableCompany` predicate.
export const getPrimaryExperience = (person: CompanyEnrichPreviewPerson) =>
	person.experiences?.find((exp) => exp.is_matched && exp.company?.id) ??
	person.experiences?.find((exp) => exp.is_current && exp.company?.id) ??
	null;

const getPrimaryExperienceCompany = (person: CompanyEnrichPreviewPerson) =>
	getPrimaryExperience(person)?.company ?? null;

// Single source of truth for the company shown on a company-enrich result:
// prefer the fully-enriched `matchedCompany`, else fall back to the nested
// experience company. Returns null when the person has no displayable company.
export const resolveDisplayCompany = (person: IPersonSummary): DisplayCompany | null => {
	if (person.matchedCompany) {
		return person.matchedCompany;
	}

	const company = getPrimaryExperienceCompany(person);

	if (!company) {
		return null;
	}

	return {
		name: company.name,
		domain: company.domain,
		logo_url: company.logo_url,
		industry: company.industry,
	};
};

// ─── Label formatters (shared across the LinkedIn result cards) ───────────────

export const formatSlug = (slug: string): string =>
	slug
		.split("-")
		.map((w) => w.charAt(0).toUpperCase() + w.slice(1))
		.join(" ");

export const formatDepartmentLabel = (id: string): string =>
	id.split("/").map(formatSlug).join(" › ");

const SENIORITY_LABEL_MAP = new Map(SENIORITY_OPTIONS.map((o) => [o.id, o.title]));

export const formatSeniorityLabel = (id: string): string =>
	SENIORITY_LABEL_MAP.get(id) ?? formatSlug(id);

export interface ParsedLinkedinUrl {
	// the original entry as typed/pasted (trailing separators trimmed)
	raw: string;
	// "in/<slug>" when valid; a truncated display fallback when not
	handle: string;
	// canonical profile URL when valid, "" otherwise — what we send to the API
	url: string;
	valid: boolean;
}

// Parse one entry from the LinkedIn URL chip input. Accepts full profile URLs
// (any scheme/www/regional subdomain, with or without query/trailing slash) and
// bare handles ("in/<slug>"). `pub` profiles are normalized to the `in/` form.
// Returns null only for empty input.
export const parseLinkedinUrl = (raw: string): ParsedLinkedinUrl | null => {
	const trimmed = (raw || "").trim().replace(/[,;]+$/, "");

	if (!trimmed) {
		return null;
	}

	const cleaned = trimmed
		.replace(/^https?:\/\//i, "")
		.replace(/^www\./i, "")
		.replace(/\?.*$/, "")
		.replace(/\/+$/, "");

	const fromUrl = cleaned.match(/^(?:[a-z]{2,3}\.)?linkedin\.com\/(?:in|pub)\/([^/]+)/i);
	const fromHandle = cleaned.match(/^in\/([^/]+)/i);
	const match = fromUrl ?? fromHandle;

	if (match) {
		const handle = `in/${decodeURIComponent(match[1])}`;

		return { raw: trimmed, handle, url: `https://www.linkedin.com/${handle}`, valid: true };
	}

	return {
		raw: trimmed,
		handle: trimmed.length > 28 ? `${trimmed.slice(0, 28)}…` : trimmed,
		url: "",
		valid: false,
	};
};
